import { Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import {
  ApiErrorStatusResponse,
  ApiResponse,
  EntityStatus,
  UserResetPasswordDto,
  UserSessionCoreDto,
  UserSessionId,
  UserTwoFATokenVaultActions,
} from '@workspace/data-types';
import {
  AlertSnackBarService,
  AuraPasswordConfirmEditComponent,
  ConfirmDialogAuraComponent,
  DialogData,
  EnumsNiceLabelUtils,
  UserTwoFAVerificationDialogAuraComponent,
} from '@workspace/ng-aura';
import { OpenIdService, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, filter, map, Observable, switchMap, tap } from 'rxjs';
import { DeactivateAccountCoreComponent } from './deactivate-account/deactivate-account.component';
import { UserSessionsCoreComponent } from './sessions/user-sessions.component';

@Component({
  selector: 'core-user-security',
  templateUrl: './user-security.component.html',
  imports: [
    AuraPasswordConfirmEditComponent,
    UserSessionsCoreComponent,
    DeactivateAccountCoreComponent,
    RouterModule,
  ],
  standalone: true,
})
export class UserSecurityCoreComponent {
  private destroyRef = inject(DestroyRef);
  private userStore = inject(UserStore);
  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private alertSnackbar = inject(AlertSnackBarService);
  private openIdService = inject(OpenIdService);

  title = input<string>();
  isLoading = true;
  currentUser = this.userStore.currentUser;
  userSessions = signal<UserSessionCoreDto[]>([]);

  isEditingPassword = signal<boolean>(false);
  isAllowedToEditPassword = signal<boolean>(false);

  constructor() {
    this.currentUser();
    this.getUserSessions();
    this.isLoading = false;

    effect(() => {
      if (this.isEditingPassword() && this.isAllowedToEditPassword() === false) {
        this.openTwoFADialog(UserTwoFATokenVaultActions.USER_TWO_FA_CHANGE_PASSWORD)
          .pipe(
            tap((success) => {
              if (success) {
                this.isAllowedToEditPassword.set(true);
              } else {
                this.isEditingPassword.set(false);
                this.isAllowedToEditPassword.set(false);
              }
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    });
  }

  savePassword(password: UserResetPasswordDto) {
    this.api
      .user()
      .patch()
      .password(password)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((response: ApiResponse) => {
          if (response.success) {
            this.userStore.updateCurrentUserCredentialsTimestamp();
            this.alertSnackbar.success({
              title: 'Modification de votre mot de passe',
              message: 'Le mot de passe de votre compte a été modifié avec succès',
            });
            this.isEditingPassword.set(false);
          }
        }),
        catchError((errorResponse) => {
          const apiError = errorResponse.error as ApiErrorStatusResponse;
          this.alertSnackbar.error({
            message: EnumsNiceLabelUtils.erroCode(apiError.statusCode, apiError.code),
            title: "Une erreur s'est produite",
          });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  getUserSessions(): void {
    this.api
      .userSessions()
      .mySessions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((userSessions: UserSessionCoreDto[]) => this.userSessions.set(userSessions)),
      )
      .subscribe();
  }

  revokeSession(userSessionId: UserSessionId): void {
    this.api
      .userSessions()
      .revoke(userSessionId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.userSessions.update((sessions) => sessions.filter((s) => s._id !== userSessionId))),
      )
      .subscribe();
  }

  revokeAllSessions(): void {
    this.dialog
      .open(ConfirmDialogAuraComponent, {
        data: {
          title: 'Suppression des sessions',
          message: 'Êtes-vous sûr de vouloir supprimer vos sessions ? Vous serez imminemment déconnecté.',
          confirm: {
            text: 'Allons-y',
            class: 'primary destructive',
          },
        } as DialogData,
      })
      .afterClosed()
      .pipe(
        filter((success) => !!success),
        switchMap(() =>
          this.api
            .userSessions()
            .revokeAllSessions()
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              tap(() => {
                this.alertSnackbar.success({
                  title: 'Suppression des sessions',
                  message: 'Vos sessions ont été supprimées avec succès',
                });
                this.openIdService.wipeOut();
              }),
            ),
        ),
      )
      .subscribe();
  }

  openDeactivateAccountFlow(): void {
    this.dialog
      .open(UserTwoFAVerificationDialogAuraComponent, {
        data: { action: UserTwoFATokenVaultActions.DEACTIVATE_ACCOUNT },
        disableClose: true, // Click outside the dialog do not close
      })
      .afterClosed()
      .pipe(
        filter((success) => !!success),
        switchMap(() =>
          this.dialog
            .open(ConfirmDialogAuraComponent, {
              data: {
                title: 'Désactiver votre compte',
                message:
                  'Êtes-vous sûr de vouloir désactiver votre compte ? Vous pourrez le réactiver ultérieurement en contactant le support.',
                confirm: {
                  text: 'Désactiver',
                  class: 'primary destructive',
                },
              } as DialogData,
            })
            .afterClosed(),
        ),
        filter((confirmed) => !!confirmed),
        switchMap(() =>
          this.api
            .user()
            .account()
            .deactivate()
            .pipe(
              tap(() => {
                this.userStore.deactivateUser();
                this.openIdService.wipeOut(false);
              }),
              catchError((errorResponse) => {
                const apiError = errorResponse.error as ApiErrorStatusResponse;
                this.alertSnackbar.error({
                  message: EnumsNiceLabelUtils.erroCode(apiError.statusCode, apiError.code),
                  title: "Une erreur s'est produite",
                });
                return EMPTY;
              }),
            ),
        ),
        tap(() => {
          void this.router.navigate(['/landing/goodbye'], {
            queryParams: { type: EntityStatus.DEACTIVATED },
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private openTwoFADialog(
    action: UserTwoFATokenVaultActions.USER_TWO_FA_CHANGE_PASSWORD,
  ): Observable<boolean> {
    return this.dialog
      .open(UserTwoFAVerificationDialogAuraComponent, {
        data: { action },
        disableClose: true,
      })
      .afterClosed()
      .pipe(
        map((success) => !!success),
        takeUntilDestroyed(this.destroyRef),
      );
  }
}
